import { useEffect, useState } from 'react';
import adminApi from '@/lib/adminApi';

const extractYouTubeId = (url = '') => {
  const value = String(url || '').trim();
  if (!value) return '';
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/|youtube\.com\/live\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{6,})/,
    /[?&]v=([A-Za-z0-9_-]{6,})/,
  ];
  for (const pattern of patterns) {
    const match = value.match(pattern);
    if (match?.[1]) return match[1];
  }
  return '';
};

const detectVideoPlatform = (url = '') => {
  try {
    const host = new URL(String(url || '').trim()).hostname.toLowerCase();
    if (host.includes('youtube.com') || host.includes('youtu.be')) return 'YouTube';
    if (host.includes('instagram.com')) return 'Instagram';
    if (host.includes('facebook.com') || host.includes('fb.watch')) return 'Facebook';
    if (host.includes('vimeo.com')) return 'Vimeo';
    if (host.includes('dailymotion.com') || host.includes('dai.ly')) return 'Dailymotion';
  } catch (error) {
    return 'Video';
  }
  return 'External video';
};

const AdminVideos = () => {
  const [videos, setVideos] = useState([]);
  const [artistImages, setArtistImages] = useState([]);
  const [collaboratorImages, setCollaboratorImages] = useState([]);
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [formData, setFormData] = useState({
    artist_id: '',
    video_title: '',
    video_url: '',
    description: '',
  });
  const [formBusy, setFormBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const [formStatus, setFormStatus] = useState('');

  const loadMedia = async () => {
    const [mediaResponse, artistsResponse] = await Promise.all([
      adminApi.get('/admin/videos'),
      adminApi.get('/admin/artists'),
    ]);

    const nextVideos = mediaResponse.data.videos || [];
    setVideos(nextVideos);
    setArtistImages(mediaResponse.data.artist_images || []);
    setCollaboratorImages(mediaResponse.data.collaborator_images || []);
    setArtists(artistsResponse.data.artists || []);
    return nextVideos;
  };

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const [mediaResponse, artistsResponse] = await Promise.all([
          adminApi.get('/admin/videos'),
          adminApi.get('/admin/artists'),
        ]);
        if (mounted) {
          setVideos(mediaResponse.data.videos || []);
          setArtistImages(mediaResponse.data.artist_images || []);
          setCollaboratorImages(mediaResponse.data.collaborator_images || []);
          setArtists(artistsResponse.data.artists || []);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, []);

  const handleDeleteVideo = async (videoId) => {
    setBusyId(`video-${videoId}`);
    try {
      await adminApi.delete(`/admin/videos/${videoId}`);
      await loadMedia();
    } finally {
      setBusyId('');
    }
  };

  const handleDeleteArtistImage = async (artistId) => {
    setBusyId(`artist-image-${artistId}`);
    try {
      await adminApi.delete(`/admin/media/artist-image/${artistId}`);
      await loadMedia();
    } finally {
      setBusyId('');
    }
  };

  const handleDeleteCollaboratorImage = async (collaboratorId) => {
    setBusyId(`collaborator-image-${collaboratorId}`);
    try {
      await adminApi.delete(`/admin/media/collaborator-image/${collaboratorId}`);
      await loadMedia();
    } finally {
      setBusyId('');
    }
  };

  const handleAddVideo = async (event) => {
    event.preventDefault();
    const payload = {
      artist_id: formData.artist_id.trim(),
      video_title: formData.video_title.trim(),
      video_url: formData.video_url.trim(),
      description: formData.description.trim(),
    };

    if (!payload.artist_id || !payload.video_title || !payload.video_url) {
      setFormError('Please select an artist and enter both video title and public video URL.');
      return;
    }

    setFormBusy(true);
    setFormError('');
    setFormStatus('');
    try {
      const response = await adminApi.post('/admin/videos', payload);
      setFormData({
        artist_id: '',
        video_title: '',
        video_url: '',
        description: '',
      });
      setFormStatus(response.data?.video ? 'Video added successfully.' : 'Video saved. Refreshing media list...');
      try {
        await loadMedia();
      } catch (refreshError) {
        setFormStatus('Video was added, but the media list could not refresh automatically. Please refresh this page.');
      }
    } catch (error) {
      const message = error.response?.data?.detail || error.message || 'Unable to add video.';
      const lowered = String(message).toLowerCase();
      if (lowered.includes('network') || lowered.includes('timeout')) {
        try {
          const refreshedVideos = await loadMedia();
          const youtubeId = extractYouTubeId(payload.video_url);
          const savedVideo = refreshedVideos.find((video) => {
            const sameArtist = video.artist_id === payload.artist_id;
            const sameId = youtubeId && video.youtube_id === youtubeId;
            const sameUrl = video.video_url === payload.video_url;
            const sameTitle = (video.video_title || '').trim().toLowerCase() === payload.video_title.toLowerCase();
            return sameArtist && (sameId || sameUrl || sameTitle);
          });
          if (savedVideo) {
            setFormData({
              artist_id: '',
              video_title: '',
              video_url: '',
              description: '',
            });
            setFormStatus('Video added successfully. The media list has been refreshed.');
            setFormError('');
          } else {
            setFormError('Network was interrupted while confirming the video. Please click Refresh or reload before adding it again.');
          }
        } catch (refreshError) {
          setFormError('Network was interrupted while confirming the video. Please click Refresh or reload before adding it again.');
        }
      } else {
        setFormError(message);
      }
    } finally {
      setFormBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <section className="bg-white rounded-[2rem] shadow-lg p-6 sm:p-8 border border-orange-100">
        <h2 className="text-3xl font-bold text-gray-900">Media Management</h2>
        <p className="text-gray-600 mt-3">
          Review all artist videos, artist profile images, and collaborator profile images in one place so the platform stays clean and safe.
        </p>
      </section>

      <section className="bg-white rounded-[2rem] shadow-lg border border-orange-100 p-5 sm:p-7">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">Add video from admin</h3>
            <p className="text-sm text-gray-500 mt-1">
              Add or restore a YouTube, Instagram, Facebook, Vimeo, or other public video directly on an artist profile.
            </p>
          </div>
          <span className="rounded-full bg-orange-50 px-4 py-2 text-xs font-semibold text-[#FF6F00]">
            Supports YouTube embeds and public social video links
          </span>
        </div>
        <form className="space-y-5" onSubmit={handleAddVideo}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm font-semibold text-gray-700">Artist</span>
              <select
                value={formData.artist_id}
                onChange={(event) => setFormData((current) => ({ ...current, artist_id: event.target.value }))}
                className="mt-2 w-full rounded-2xl border border-gray-300 px-4 py-3 bg-white"
                required
              >
                <option value="">Select artist</option>
                {artists.map((artist) => (
                  <option key={artist.id} value={artist.id}>
                    {artist.name} ({artist.category || 'Uncategorized'})
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-sm font-semibold text-gray-700">Video title</span>
              <input
                value={formData.video_title}
                onChange={(event) => setFormData((current) => ({ ...current, video_title: event.target.value }))}
                placeholder="e.g. Live Kathak performance"
                className="mt-2 w-full rounded-2xl border border-gray-300 px-4 py-3"
                required
              />
            </label>
            <label className="block lg:col-span-2">
              <span className="text-sm font-semibold text-gray-700">Video URL</span>
              <input
                value={formData.video_url}
                onChange={(event) => setFormData((current) => ({ ...current, video_url: event.target.value }))}
                placeholder="YouTube, Instagram, Facebook, Vimeo, or public video URL"
                className="mt-2 w-full rounded-2xl border border-gray-300 px-4 py-3"
                required
              />
              <span className="mt-2 block text-xs text-gray-500">
                YouTube videos embed on profile. Instagram/Facebook and other platform links open as clean video cards when embeds are restricted by the platform.
              </span>
            </label>
            <label className="block lg:col-span-2">
              <span className="text-sm font-semibold text-gray-700">Short description</span>
              <textarea
                value={formData.description}
                onChange={(event) => setFormData((current) => ({ ...current, description: event.target.value }))}
                placeholder="Optional note shown inside admin media management"
                className="mt-2 min-h-[96px] w-full rounded-2xl border border-gray-300 px-4 py-3"
              />
            </label>
          </div>
          {formError && (
            <div className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
              {formError}
            </div>
          )}
          {formStatus && (
            <div className="rounded-2xl border border-green-100 bg-green-50 px-4 py-3 text-sm font-medium text-green-700">
              {formStatus}
            </div>
          )}
          <div className="flex flex-wrap gap-3">
          <button type="submit" className="btn-primary" disabled={formBusy}>
            {formBusy ? 'Adding video...' : 'Add Video'}
          </button>
            <button type="button" className="btn-outline" onClick={loadMedia} disabled={formBusy}>
              Refresh media
            </button>
          </div>
        </form>
      </section>

      <section className="grid gap-4">
        {loading && <div className="text-gray-500">Loading media...</div>}
        {!loading && videos.length === 0 && (
          <div className="bg-white rounded-[2rem] shadow-lg p-6 border border-orange-100 text-gray-500">
            No videos have been submitted yet.
          </div>
        )}
        {videos.map((video) => (
          <article key={video.id} className="bg-white rounded-[2rem] shadow-lg p-6 border border-orange-100">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">{video.video_title}</h3>
                <p className="text-sm text-gray-500 mt-2">Artist: {video.artist_name}</p>
                <a
                  href={video.video_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block mt-3 text-sm font-semibold text-[#4A148C] hover:text-[#FF6F00]"
                >
                  Open {video.video_platform || detectVideoPlatform(video.video_url)} link
                </a>
                {video.description && <p className="text-sm text-gray-600 mt-4">{video.description}</p>}
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm min-w-[220px]">
                <div className="rounded-2xl bg-gray-50 px-4 py-3">
                  <p className="text-gray-500">Artist ID</p>
                  <p className="font-semibold text-gray-800 mt-1 break-all">{video.artist_id}</p>
                </div>
                <div className="rounded-2xl bg-gray-50 px-4 py-3">
                  <p className="text-gray-500">Platform</p>
                  <p className="font-semibold text-gray-800 mt-1 break-all">{video.video_platform || detectVideoPlatform(video.video_url)}</p>
                </div>
              </div>
            </div>
            <div className="mt-5">
              <button
                type="button"
                className="rounded-full px-4 py-2 text-sm font-semibold border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 transition disabled:opacity-60"
                onClick={() => handleDeleteVideo(video.id)}
                disabled={busyId === `video-${video.id}`}
              >
                Delete Video
              </button>
            </div>
          </article>
        ))}
      </section>

      <section className="grid gap-4 xl:grid-cols-2">
        <div className="bg-white rounded-[2rem] shadow-lg p-6 border border-orange-100">
          <div className="flex items-center justify-between gap-4 mb-5">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">Artist profile images</h3>
              <p className="text-sm text-gray-500 mt-1">Remove any unsuitable artist profile photo.</p>
            </div>
            <span className="rounded-full bg-orange-50 px-4 py-2 text-sm font-semibold text-[#FF6F00]">{artistImages.length}</span>
          </div>
          <div className="space-y-4">
            {!loading && artistImages.length === 0 && <p className="text-sm text-gray-500">No artist profile images uploaded yet.</p>}
            {artistImages.map((artist) => (
              <article key={artist.id} className="rounded-[1.5rem] border border-orange-100 p-4">
                <div className="flex items-start gap-4">
                  <img src={artist.image_url} alt={artist.name} className="w-20 h-20 rounded-2xl object-contain bg-gray-50 border border-gray-200" />
                  <div className="flex-1 min-w-0">
                    <h4 className="text-lg font-semibold text-gray-900">{artist.name}</h4>
                    <p className="text-sm text-gray-500 mt-1">{artist.category || 'Artist'}</p>
                    <p className="text-xs text-gray-500 mt-1 break-all">{artist.email}</p>
                  </div>
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-full px-4 py-2 text-sm font-semibold border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 transition disabled:opacity-60"
                  onClick={() => handleDeleteArtistImage(artist.id)}
                  disabled={busyId === `artist-image-${artist.id}`}
                >
                  Remove Image
                </button>
              </article>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-[2rem] shadow-lg p-6 border border-orange-100">
          <div className="flex items-center justify-between gap-4 mb-5">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">Collaborator profile images</h3>
              <p className="text-sm text-gray-500 mt-1">Moderate organization and client profile photos too.</p>
            </div>
            <span className="rounded-full bg-purple-50 px-4 py-2 text-sm font-semibold text-[#4A148C]">{collaboratorImages.length}</span>
          </div>
          <div className="space-y-4">
            {!loading && collaboratorImages.length === 0 && <p className="text-sm text-gray-500">No collaborator profile images uploaded yet.</p>}
            {collaboratorImages.map((collaborator) => (
              <article key={collaborator.id} className="rounded-[1.5rem] border border-orange-100 p-4">
                <div className="flex items-start gap-4">
                  <img src={collaborator.image_url} alt={collaborator.organization_name} className="w-20 h-20 rounded-2xl object-contain bg-gray-50 border border-gray-200" />
                  <div className="flex-1 min-w-0">
                    <h4 className="text-lg font-semibold text-gray-900">{collaborator.organization_name}</h4>
                    <p className="text-sm text-gray-500 mt-1">{collaborator.organization_type || collaborator.name}</p>
                    <p className="text-xs text-gray-500 mt-1 break-all">{collaborator.email}</p>
                  </div>
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-full px-4 py-2 text-sm font-semibold border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 transition disabled:opacity-60"
                  onClick={() => handleDeleteCollaboratorImage(collaborator.id)}
                  disabled={busyId === `collaborator-image-${collaborator.id}`}
                >
                  Remove Image
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminVideos;
